/**
 * SharePromptCard - "Please share this app" happy-moment card
 * 
 * Shows after successful actions like sending an estimate.
 * Throttled to once per calendar day via localStorage.
 */

import { useState } from "react";
import { X, Copy, Check } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { markShownToday } from "../utils/sharePrompt";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SharePromptCard({ onClose }) {
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleDismiss = () => {
    markShownToday();
    onClose();
  };

  const handleCopyLink = async () => {
    setCopying(true);
    
    try {
      const response = await fetch(`${API}/api/referral/token`, {
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch referral link");
      }
      
      const data = await response.json();
      const url = data.referral_url;
      
      // Copy to clipboard with proper error handling
      let copySuccess = false;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          copySuccess = true;
        }
      } catch (clipboardError) {
        console.warn("Clipboard API failed, trying fallback:", clipboardError);
      }
      
      // Fallback for when clipboard API fails or isn't available
      if (!copySuccess) {
        try {
          const textarea = document.createElement("textarea");
          textarea.value = url;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
          copySuccess = true;
        } catch (fallbackError) {
          console.warn("Fallback copy failed:", fallbackError);
        }
      }
      
      if (copySuccess) {
        setCopied(true);
        toast.success("Link copied!");
        markShownToday();
        
        // Close after brief delay to show success state
        setTimeout(() => {
          onClose();
        }, 1200);
      } else {
        // If all methods fail, show the URL to user
        toast.error("Couldn't copy automatically. URL: " + url.substring(0, 40) + "...");
        markShownToday();
        setTimeout(() => {
          onClose();
        }, 3000);
      }
      
    } catch (error) {
      console.error("Error copying share link:", error);
      toast.error("Couldn't load link. Please try again.");
    } finally {
      setCopying(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
      data-testid="share-prompt-overlay"
    >
      <div 
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 animate-in slide-in-from-bottom-4 duration-300"
        data-testid="share-prompt-card"
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Dismiss"
          data-testid="share-prompt-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Content */}
        <div className="text-center pr-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-testid="share-prompt-title">
            🧡 Please share this app
          </h3>
          
          <p className="text-sm text-gray-600 mb-2 leading-relaxed">
            If Free Service Pro helped you today, sharing it with another contractor is the biggest way you can help us grow.
          </p>
          
          <p className="text-xs text-gray-400 mb-5 italic">
            We&apos;re working on referral rewards.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-2">
          <Button
            onClick={handleCopyLink}
            disabled={copying || copied}
            className="w-full bg-[#FF6600] hover:bg-[#E55C00] text-white font-medium h-11"
            data-testid="share-prompt-copy-btn"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Copied!
              </>
            ) : copying ? (
              "Loading..."
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copy your share link
              </>
            )}
          </Button>
          
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="w-full text-gray-500 hover:text-gray-700 font-normal h-10"
            data-testid="share-prompt-dismiss-btn"
          >
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}
